import type { DxfPart, NestingResult, NestSheet, Placement } from './dxfNesting';
import { entityToSvgPaths } from './dxfNesting';

export interface RasterMask {
  cols: number;
  rows: number;
  cells: Uint8Array;
  cellSize: number;
  filledCells: number;
}

function dilateBinary(cells: Uint8Array, cols: number, rows: number, r: number): Uint8Array {
  if (r <= 0) return cells;
  let cur = cells;
  for (let k = 0; k < r; k++) {
    const next = new Uint8Array(cur.length);
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const idx = j * cols + i;
        if (cur[idx]) {
          next[idx] = 1;
          continue;
        }
        let hit = 0;
        for (let dj = -1; dj <= 1 && !hit; dj++) {
          for (let di = -1; di <= 1 && !hit; di++) {
            const ni = i + di, nj = j + dj;
            if (ni >= 0 && ni < cols && nj >= 0 && nj < rows && cur[nj * cols + ni]) hit = 1;
          }
        }
        next[idx] = hit;
      }
    }
    cur = next;
  }
  return cur;
}

/**
 * True 90° clockwise rotation of the occupancy grid (matches SVG `rotate(90)` on part paths).
 * NOT a matrix transpose — transpose is a reflection and gives the wrong footprint for
 * asymmetric parts, so collision masks were smaller than rendered geometry → overlaps in preview.
 */
function rotateMask90Clockwise(m: RasterMask): RasterMask {
  const { cols, rows, cells, cellSize } = m;
  const newCols = rows;
  const newRows = cols;
  const out = new Uint8Array(newCols * newRows);
  let filled = 0;
  for (let oj = 0; oj < rows; oj++) {
    for (let oi = 0; oi < cols; oi++) {
      const v = cells[oj * cols + oi];
      const ni = rows - 1 - oj;
      const nj = oi;
      const idx = nj * newCols + ni;
      out[idx] = v;
      if (v) filled++;
    }
  }
  return { cols: newCols, rows: newRows, cells: out, cellSize, filledCells: filled };
}

export function buildPartMask(part: DxfPart, cellSizeMm: number): RasterMask {
  const { bbox, rawEntities, blocks } = part;
  const W = bbox.width;
  const H = bbox.height;
  if (W <= 0 || H <= 0) throw new Error('Part has zero bounding box');
  const cols = Math.max(1, Math.ceil(W / cellSizeMm));
  const rows = Math.max(1, Math.ceil(H / cellSizeMm));

  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D not available');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cols, rows);
  ctx.fillStyle = '#000000';
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = Math.max(1.2, Math.min(cols, rows) / 120);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.scale(cols / W, rows / H);

  for (const e of rawEntities) {
    const ds = entityToSvgPaths(e, bbox.minX, bbox.minY, bbox.height, blocks);
    for (const d of ds) {
      try {
        const p = new Path2D(d);
        ctx.fill(p);
        ctx.stroke(p);
      } catch {
        /* ignore */
      }
    }
  }

  const img = ctx.getImageData(0, 0, cols, rows);
  const cells = new Uint8Array(cols * rows);
  let filled = 0;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const idx = j * cols + i;
      if (img.data[idx * 4] < 220) {
        cells[idx] = 1;
        filled++;
      }
    }
  }

  return { cols, rows, cells, cellSize: cellSizeMm, filledCells: filled };
}

function canPlace(
  mask: Uint8Array, mc: number, mr: number,
  occ: Uint8Array, sheetCols: number, sheetRows: number,
  mx: number, my: number,
): boolean {
  if (mx < 0 || my < 0 || mx + mc > sheetCols || my + mr > sheetRows) return false;
  for (let j = 0; j < mr; j++) {
    const rowOcc = (my + j) * sheetCols;
    const rowM = j * mc;
    for (let i = 0; i < mc; i++) {
      if (!mask[rowM + i]) continue;
      if (occ[rowOcc + mx + i]) return false;
    }
  }
  return true;
}

function stamp(mask: Uint8Array, mc: number, mr: number, occ: Uint8Array, sheetCols: number, mx: number, my: number): void {
  for (let j = 0; j < mr; j++) {
    const rowOcc = (my + j) * sheetCols;
    const rowM = j * mc;
    for (let i = 0; i < mc; i++) {
      if (mask[rowM + i]) occ[rowOcc + mx + i] = 1;
    }
  }
}

interface Instance {
  partId: string;
  copyIndex: number;
  filledCells: number;
  bboxW: number;
  bboxH: number;
  place0: RasterMask;
  place90: RasterMask | null;
}

function prepareInstance(
  part: DxfPart,
  copyIndex: number,
  raw: RasterMask,
  cellSize: number,
  gapSteps: number,
  allowRotation: boolean,
): Instance {
  const dil0 = gapSteps > 0 ? dilateBinary(raw.cells, raw.cols, raw.rows, gapSteps) : raw.cells;
  const place0: RasterMask = { ...raw, cells: dil0 };

  let place90: RasterMask | null = null;
  if (allowRotation) {
    const r = rotateMask90Clockwise(raw);
    const dilT = gapSteps > 0 ? dilateBinary(r.cells, r.cols, r.rows, gapSteps) : r.cells;
    place90 = { ...r, cells: dilT };
  }

  return {
    partId: part.id,
    copyIndex,
    filledCells: raw.filledCells,
    bboxW: part.bbox.width,
    bboxH: part.bbox.height,
    place0,
    place90,
  };
}

function scorePos(my: number, mx: number, sheetCols: number): number {
  return my * sheetCols + mx;
}

function bestSpotForMask(
  m: RasterMask,
  occ: Uint8Array,
  sheetCols: number,
  sheetRows: number,
): { mx: number; my: number; sc: number } | null {
  const mc = m.cols;
  const mr = m.rows;
  if (mc > sheetCols || mr > sheetRows) return null;
  let best: { mx: number; my: number; sc: number } | null = null;
  for (let my = 0; my <= sheetRows - mr; my++) {
    for (let mx = 0; mx <= sheetCols - mc; mx++) {
      if (!canPlace(m.cells, mc, mr, occ, sheetCols, sheetRows, mx, my)) continue;
      const sc = scorePos(my, mx, sheetCols);
      if (!best || sc < best.sc) best = { mx, my, sc };
    }
  }
  return best;
}

function findBestPosition(
  inst: Instance,
  occ: Uint8Array,
  sheetCols: number,
  sheetRows: number,
  allowRotation: boolean,
): { mx: number; my: number; rotated: boolean; used: RasterMask } | null {
  const a = bestSpotForMask(inst.place0, occ, sheetCols, sheetRows);
  const b = allowRotation && inst.place90
    ? bestSpotForMask(inst.place90, occ, sheetCols, sheetRows)
    : null;

  if (!a && !b) return null;
  if (!a) return { mx: b!.mx, my: b!.my, rotated: true, used: inst.place90! };
  if (!b) return { mx: a.mx, my: a.my, rotated: false, used: inst.place0 };
  if (a.sc < b.sc) return { mx: a.mx, my: a.my, rotated: false, used: inst.place0 };
  if (b.sc < a.sc) return { mx: b.mx, my: b.my, rotated: true, used: inst.place90! };
  return { mx: a.mx, my: a.my, rotated: false, used: inst.place0 };
}

function defaultCellSize(sheetW: number, sheetH: number): number {
  const m = Math.min(sheetW, sheetH);
  return Math.max(2, Math.min(6, Math.round(m / 350)));
}

export interface ShapeNestOptions {
  sheetWidth: number;
  sheetHeight: number;
  spacing: number;
  allowRotation: boolean;
  cellSizeMm?: number;
}

export function nestPartsShape(
  parts: Array<{ part: DxfPart; quantity: number }>,
  opts: ShapeNestOptions,
): NestingResult {
  const { sheetWidth, sheetHeight, spacing, allowRotation } = opts;
  const cellSize = opts.cellSizeMm ?? defaultCellSize(sheetWidth, sheetHeight);
  const gapSteps = Math.max(0, Math.ceil(spacing / cellSize));

  const rawByPartId = new Map<string, RasterMask>();
  for (const { part } of parts) {
    if (!rawByPartId.has(part.id)) {
      rawByPartId.set(part.id, buildPartMask(part, cellSize));
    }
  }

  const filledByPartId = new Map<string, number>();
  rawByPartId.forEach((raw, id) => {
    filledByPartId.set(id, raw.filledCells);
  });

  const instances: Instance[] = [];
  for (const { part, quantity } of parts) {
    const raw = rawByPartId.get(part.id)!;
    for (let q = 0; q < quantity; q++) {
      instances.push(prepareInstance(part, q, raw, cellSize, gapSteps, allowRotation));
    }
  }

  instances.sort((a, b) => b.filledCells - a.filledCells);

  const sheetCols = Math.max(1, Math.ceil(sheetWidth / cellSize));
  const sheetRows = Math.max(1, Math.ceil(sheetHeight / cellSize));

  const sheets: NestSheet[] = [];
  let remaining = [...instances];
  const cellArea = cellSize * cellSize;

  while (remaining.length > 0) {
    const occ = new Uint8Array(sheetCols * sheetRows);
    const placements: Placement[] = [];
    let progress = true;

    while (progress) {
      progress = false;
      for (let ri = 0; ri < remaining.length; ri++) {
        const inst = remaining[ri];
        const found = findBestPosition(inst, occ, sheetCols, sheetRows, allowRotation);
        if (!found) continue;

        stamp(found.used.cells, found.used.cols, found.used.rows, occ, sheetCols, found.mx, found.my);
        placements.push({
          partId: inst.partId,
          copyIndex: inst.copyIndex,
          x: found.mx * cellSize,
          y: found.my * cellSize,
          width: found.rotated ? inst.bboxH : inst.bboxW,
          height: found.rotated ? inst.bboxW : inst.bboxH,
          rotated: found.rotated,
        });
        remaining.splice(ri, 1);
        progress = true;
        break;
      }
    }

    if (placements.length === 0) {
      break;
    }

    let sheetFilledCells = 0;
    for (const pl of placements) {
      sheetFilledCells += filledByPartId.get(pl.partId) ?? 0;
    }
    const utilization = sheetWidth * sheetHeight > 0
      ? (sheetFilledCells * cellArea) / (sheetWidth * sheetHeight) * 100
      : 0;
    sheets.push({ placements, utilization });
  }

  const totalInstances = parts.reduce((s, p) => s + p.quantity, 0);
  const placedParts = sheets.reduce((s, sh) => s + sh.placements.length, 0);

  let totalFilledCells = 0;
  for (const sh of sheets) {
    for (const pl of sh.placements) {
      totalFilledCells += filledByPartId.get(pl.partId) ?? 0;
    }
  }
  const totalSheetArea = sheets.length * sheetWidth * sheetHeight;
  const overallUtilization = totalSheetArea > 0 ? (totalFilledCells * cellArea) / totalSheetArea * 100 : 0;

  return {
    sheets,
    totalParts: totalInstances,
    placedParts,
    totalSheets: sheets.length,
    overallUtilization,
  };
}
